import { createMuiTheme } from '@material-ui/core/styles';
import { fade } from '@material-ui/core/styles/colorManipulator';
import ArrowDropDownRoundedIcon from '@material-ui/icons/ArrowDropDownRounded';

// Color definitions
export const blue = {
  50: "#F0F7FF",
  100: "#C2E0FF",
  200: "#99CCF3",
  300: "#66B2FF",
  400: "#3399FF",
  500: "#007FFF",
  600: "#0072E5",
  700: "#0059B2",
  800: "#004C99",
  900: "#003A75",
  main: "#0069EA"
};

export const blueDark = {
  50: "#E2EDF8",
  100: "#CEE0F3",
  200: "#91B9E3",
  300: "#5090D3",
  400: "#265D97",
  500: "#1E4976",
  600: "#173A5E",
  700: "#132F4C",
  800: "#001E3C",
  900: "#0A1929",
  main: "#5090D3"
};

const grey = {
  50: "#F3F6F9",
  100: "#E7EBF0",
  200: "#E0E3E7",
  300: "#CDD2D7",
  400: "#B2BAC2",
  500: "#A0AAB4",
  600: "#6F7E8C",
  700: "#3E5060",
  800: "#2D3843",
  900: "#1A2027"
};

const systemFont = [
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"'
];

// Helper function to check if dark mode should be active (5pm-8am local time)
export const shouldUseDarkMode = () => {
  const now = new Date();
  const hours = now.getHours();
  // Dark mode from 5pm (17:00) to 8am (08:00)
  return hours >= 17 || hours < 8;
};

// Get meta theme color for browser
export const getMetaThemeColor = (mode) => {
  const themeColor = {
    light: grey[50],
    dark: blueDark[800]
  };
  return themeColor[mode];
};

// Get design tokens for light or dark mode
export const getDesignTokens = (mode) => {
  const baseTheme = createMuiTheme();
  
  return {
    palette: {
      type: mode, // MUI v4 uses 'type' instead of 'mode'
      primary: {
        ...blue,
        main: mode === "dark" ? blue[400] : blue.main
      },
      secondary: {
        main: "#ff5555"
      },
      success: {
        50: "#E9FBF0",
        100: "#C6F6D9",
        200: "#9AEFBC",
        300: "#6AE79C",
        400: "#3EE07F",
        500: "#21CC66",
        600: "#1DB45A",
        700: "#1AA251",
        800: "#178D46",
        900: "#0F5C2E",
        main: mode === "dark" ? "#1DB45A" : "#1AA251"
      },
      error: {
        50: "#FFF0F1",
        100: "#FFDBDE",
        200: "#FFBDC2",
        300: "#FF99A2",
        400: "#FF7A86",
        500: "#FF505F",
        600: "#EB0014",
        700: "#C70011",
        800: "#94000D",
        900: "#570007",
        main: "#EB0014"
      },
      warning: {
        50: "#FFF9EB",
        100: "#FFF3C1",
        200: "#FFECA1",
        300: "#FFDC48",
        400: "#F4C000",
        500: "#DEA500",
        600: "#D18E00",
        700: "#AB6800",
        800: "#8C5800",
        900: "#5A3600",
        main: "#DEA500"
      },
      divider: mode === "dark" ? fade(blue[100], 0.08) : grey[100],
      common: {
        black: "#1D1D1D",
        white: "#fff"
      },
      ...(mode === "light" && {
        background: {
          default: "#fff",
          paper: "#fff"
        },
        text: {
          primary: grey[900],
          secondary: grey[700]
        }
      }),
      ...(mode === "dark" && {
        background: {
          default: blueDark[800],
          paper: blueDark[900]
        },
        text: {
          primary: "#fff",
          secondary: grey[400]
        }
      }),
      grey: grey
    },
    shape: {
      borderRadius: 10
    },
    spacing: 10,
    typography: {
      fontFamily: ['"IBM Plex Sans"', ...systemFont].join(","),
      fontFamilyCode: [
        "Consolas",
        "Menlo",
        "Monaco",
        "Andale Mono",
        "Ubuntu Mono",
        "monospace"
      ].join(","),
      fontFamilyTagline: ['"PlusJakartaSans-ExtraBold"', ...systemFont].join(","),
      fontFamilySystem: systemFont.join(","),
      fontWeightSemiBold: 600,
      fontWeightExtraBold: 800,
      h1: {
        fontFamily: ['"PlusJakartaSans-ExtraBold"', ...systemFont].join(","),
        fontSize: "clamp(2.625rem, 1.2857rem + 3.5714vw, 4rem)",
        fontWeight: 800,
        lineHeight: 78 / 70,
        ...(mode === "light" && {
          color: blueDark[900]
        })
      },
      h2: {
        fontFamily: ['"PlusJakartaSans-ExtraBold"', ...systemFont].join(","),
        fontSize: "clamp(1.5rem, 0.9643rem + 1.4286vw, 2.25rem)",
        fontWeight: 800,
        lineHeight: 44 / 36,
        color: mode === "dark" ? grey[100] : blueDark[700]
      },
      h3: {
        fontFamily: ['"PlusJakartaSans-Bold"', ...systemFont].join(","),
        fontSize: baseTheme.typography.pxToRem(36),
        lineHeight: 44 / 36,
        letterSpacing: 0.2
      },
      h4: {
        fontFamily: ['"PlusJakartaSans-Bold"', ...systemFont].join(","),
        fontSize: baseTheme.typography.pxToRem(28),
        lineHeight: 42 / 28,
        letterSpacing: 0.2
      },
      h5: {
        fontFamily: ['"PlusJakartaSans-Bold"', ...systemFont].join(","),
        fontSize: baseTheme.typography.pxToRem(24),
        lineHeight: 36 / 24,
        letterSpacing: 0.1,
        color: mode === "dark" ? blue[300] : blue.main
      },
      h6: {
        fontSize: baseTheme.typography.pxToRem(20),
        lineHeight: 30 / 20
      },
      button: {
        textTransform: "initial",
        fontWeight: 700,
        letterSpacing: 0
      },
      subtitle1: {
        fontSize: baseTheme.typography.pxToRem(18),
        lineHeight: 24 / 18,
        letterSpacing: 0,
        fontWeight: 500
      },
      body1: {
        fontSize: baseTheme.typography.pxToRem(16),
        lineHeight: 24 / 16,
        letterSpacing: 0
      },
      body2: {
        fontSize: baseTheme.typography.pxToRem(14),
        lineHeight: 21 / 14,
        letterSpacing: 0
      },
      caption: {
        display: "inline-block",
        fontSize: baseTheme.typography.pxToRem(12),
        lineHeight: 18 / 12,
        letterSpacing: 0,
        fontWeight: 700
      }
    }
  };
};

// Get themed components for MUI v4
export const getThemedComponents = (theme) => {
  // Note: MUI v4 uses 'overrides' instead of 'components'
  // This function returns the overrides object
  return {
    overrides: {
      MuiButton: {
        sizeLarge: {
          padding: "0.875rem 1rem",
          ...theme.typography.body1,
          lineHeight: 21 / 16,
          fontWeight: 700
        },
        sizeSmall: {
          padding: theme.spacing(0.5, 1),
          marginLeft: theme.spacing(-1)
        },
        containedPrimary: {
          backgroundColor: theme.palette.primary[500] || theme.palette.primary.main,
          color: "#fff"
        }
      },
      MuiIconButton: {
        root: {
          color: theme.palette.type === "dark" 
            ? theme.palette.primary[300] || blue[300]
            : theme.palette.primary[500] || blue[500]
        },
        colorPrimary: {
          height: 34,
          width: 34,
          border: `1px solid ${
            theme.palette.type === "dark"
              ? blueDark[700]
              : grey[200]
          }`,
          borderRadius: theme.shape.borderRadius,
          color: theme.palette.type === "dark"
            ? theme.palette.primary[300] || blue[300]
            : theme.palette.primary[500] || blue[500],
          "&:hover": {
            borderColor: theme.palette.type === "dark"
              ? blueDark[600]
              : grey[300],
            background: theme.palette.type === "dark"
              ? fade(blueDark[700], 0.4)
              : grey[50]
          }
        }
      },
      MuiMenu: {
        paper: {
          marginTop: theme.spacing(0.5),
          minWidth: 160,
          elevation: 0,
          color: theme.palette.text.secondary,
          backgroundImage: "none",
          backgroundColor: theme.palette.type === "dark"
            ? blueDark[900]
            : theme.palette.background.paper,
          border: `1px solid ${
            theme.palette.type === "dark"
              ? blueDark[700]
              : grey[200]
          }`,
          "& .MuiMenuItem-root": {
            fontSize: theme.typography.pxToRem(14),
            fontWeight: 500,
            "&:hover": {
              backgroundColor: theme.palette.type === "dark"
                ? fade(blueDark[700], 0.4)
                : grey[50]
            },
            "&:focus": {
              backgroundColor: theme.palette.type === "dark"
                ? fade(blueDark[700], 0.4)
                : grey[50]
            },
            "&.Mui-selected": {
              fontWeight: 500,
              color: theme.palette.type === "dark"
                ? theme.palette.primary[300] || blue[300]
                : theme.palette.primary[600] || blue[600],
              backgroundColor: theme.palette.type === "dark"
                ? blueDark[700]
                : fade(theme.palette.primary[100] || blue[100], 0.6)
            }
          }
        }
      },
      MuiPopover: {
        paper: {
          boxShadow: `0px 4px 20px ${
            theme.palette.type === "dark"
              ? "rgba(0, 0, 0, 0.5)"
              : "rgba(170, 180, 190, 0.3)"
          }`
        }
      },
      MuiContainer: {
        root: {
          [theme.breakpoints.up("md")]: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2)
          }
        }
      },
      MuiDivider: {
        root: {
          borderColor: theme.palette.type === "dark"
            ? fade(blue[100], 0.08)
            : grey[100]
        }
      },
      MuiLink: {
        root: {
          underline: "none",
          color: theme.palette.type === "dark"
            ? theme.palette.primary[300] || blue[300]
            : theme.palette.primary[600] || blue[600],
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          "&:hover": {
            color: theme.palette.type === "dark"
              ? theme.palette.primary[200] || blue[200]
              : theme.palette.primary[700] || blue[700]
          },
          "&.MuiTypography-body1 > svg": {
            marginTop: 2
          },
          "& svg:last-child": {
            marginLeft: 2
          }
        }
      },
      MuiChip: {
        root: {
          fontWeight: 500
        },
        outlined: {
          color: theme.palette.type === "dark"
            ? grey[300]
            : grey[900],
          backgroundColor: "transparent",
          borderColor: theme.palette.type === "dark"
            ? fade(grey[100], 0.1)
            : grey[200]
        },
        filled: {
          border: "1px solid transparent",
          color: theme.palette.type === "dark"
            ? "#fff"
            : blue[800],
          backgroundColor: theme.palette.type === "dark"
            ? blueDark[500]
            : blue[100],
          "&:hover": {
            backgroundColor: theme.palette.type === "dark"
              ? blueDark[600]
              : blue[200]
          }
        },
        deleteIcon: {
          color: theme.palette.type === "dark"
            ? "#fff"
            : blue[700],
          "&:hover": {
            color: theme.palette.type === "dark"
              ? grey[100]
              : blue[900]
          }
        }
      },
      MuiList: {
        root: {
          padding: 0
        }
      },
      MuiSelect: {
        root: {
          "& .MuiSelect-icon": {
            color: theme.palette.text.secondary
          }
        },
        icon: {
          top: "calc(50% - .25em)"
        }
      },
      MuiTab: {
        root: {
          // disableTouchRipple is a prop, not a style override
        }
      },
      MuiPaper: {
        root: {
          backgroundImage: "none",
          backgroundColor: theme.palette.type === "dark"
            ? blueDark[900]
            : "#fff",
          "&[href]": {
            textDecorationLine: "none"
          }
        },
        outlined: {
          display: "block",
          borderColor: theme.palette.type === "dark"
            ? blueDark[500]
            : grey[200],
          ...(theme.palette.type === "dark" && {
            backgroundColor: blueDark[700]
          }),
          "& a, & button": {
            "&:hover": {
              boxShadow: `0px 4px 20px ${
                theme.palette.type === "dark"
                  ? "rgba(0, 0, 0, 0.5)"
                  : "rgba(170, 180, 190, 0.3)"
              }`
            }
          }
        }
      },
      MuiTableCell: {
        root: {
          padding: theme.spacing(1, 2),
          borderColor: theme.palette.divider
        },
        head: {
          color: theme.palette.text.primary,
          fontWeight: 700
        },
        body: {
          color: theme.palette.text.secondary
        }
      },
      MuiToggleButtonGroup: {
        root: {
          backgroundColor: theme.palette.type === "dark"
            ? blueDark[900]
            : "#fff"
        }
      },
      MuiToggleButton: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          color: theme.palette.type === "dark"
            ? grey[300]
            : grey[700],
          borderColor: theme.palette.type === "dark"
            ? blueDark[500]
            : grey[200],
          "&.Mui-selected": {
            color: theme.palette.type === "dark"
              ? "#fff"
              : blue[500],
            borderColor: theme.palette.type === "dark"
              ? `${blue[700]} !important`
              : `${blue[500]} !important`,
            backgroundColor: theme.palette.type === "dark"
              ? blueDark[700]
              : blue[50],
            "&:hover": {
              backgroundColor: theme.palette.type === "dark"
                ? blueDark[600]
                : blue[100]
            }
          }
        }
      },
      MuiTooltip: {
        tooltip: {
          padding: "5px 9px"
        }
      },
      MuiSwitch: {
        root: {
          width: 32,
          height: 20,
          padding: 0,
          "& .MuiSwitch-switchBase": {
            "&.Mui-checked": {
              transform: "translateX(11px)",
              color: "#fff"
            }
          }
        },
        switchBase: {
          height: 20,
          width: 20,
          padding: 0,
          color: "#fff",
          "&.Mui-checked + .MuiSwitch-track": {
            opacity: 1
          }
        },
        track: {
          opacity: 1,
          borderRadius: 32,
          backgroundColor: theme.palette.type === "dark"
            ? grey[800]
            : grey[400]
        },
        thumb: {
          flexShrink: 0,
          width: "14px",
          height: "14px"
        }
      },
      MuiAppBar: {
        root: {
          backgroundColor: theme.palette.type === "dark"
            ? blueDark[900]
            : theme.palette.background.paper,
          color: theme.palette.type === "dark"
            ? "whitesmoke"
            : "black"
        }
      },
      MuiStepIcon: {
        root: {
          '&$completed': {
            color: theme.palette.success.main,
          },
          '&$error': {
            color: theme.palette.error.main,
          },
          '&$active': {
            color: theme.palette.primary.main,
          },
        },
        active: {},
        completed: {},
        error: {}
      },
      MuiAccordion: {
        root: {
          backgroundColor: theme.palette.background.paper,
          '&:before': {
            backgroundColor: theme.palette.divider
          }
        }
      },
      MuiAccordionSummary: {
        root: {
          backgroundColor: theme.palette.background.paper,
          '&$expanded': {
            backgroundColor: theme.palette.background.paper
          }
        },
        content: {
          color: theme.palette.text.primary,
          '&$expanded': {
            color: theme.palette.text.primary
          }
        },
        expanded: {}
      },
      MuiAccordionDetails: {
        root: {
          backgroundColor: theme.palette.background.paper,
          padding: theme.spacing(2)
        }
      }
    },
    props: {
      MuiButtonBase: {
        disableTouchRipple: true
      },
      MuiButton: {
        disableElevation: true
      },
      MuiLink: {
        underline: "none"
      },
      MuiSelect: {
        IconComponent: ArrowDropDownRoundedIcon
      },
      MuiTab: {
        disableTouchRipple: true
      }
    }
  };
};

// Create and export themes
export const createBrandingTheme = (mode = "light") => {
  const designTokens = getDesignTokens(mode);
  const baseTheme = createMuiTheme(designTokens);
  const themedComponents = getThemedComponents(baseTheme);
  
  // Merge theme with component overrides for MUI v4
  return createMuiTheme({
    ...designTokens,
    overrides: themedComponents.overrides,
    props: themedComponents.props || {}
  });
};

// Export themes for direct use
export const brandingLightTheme = createBrandingTheme("light");
export const brandingDarkTheme = createBrandingTheme("dark");

// Get theme based on current time (dark mode 6pm-7am)
export const getBrandingTheme = () => {
  const mode = shouldUseDarkMode() ? "dark" : "light";
  return createBrandingTheme(mode);
};

